import { useState } from 'react';
import { Phone, CheckCircle, MapPin, Calendar, Clock, Star, ArrowRight } from 'lucide-react';

export default function FurnaceInspection() {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    systemAge: '',
    preferredDate: '',
    timeWindow: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Furnace inspection booking (demo only):', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        address: '',
        phone: '',
        email: '',
        systemAge: '',
        preferredDate: '',
        timeWindow: ''
      });
    }, 5000);
  };

  const testimonials = [
    {
      text: "We got our furnace inspected in October and the tech found a small crack in the heat exchanger. Replacing it under warranty cost nothing extra now instead of thousands mid-winter. Glad we didn't put it off.",
      author: "Robert D.",
      location: "Markham"
    },
    {
      text: "Professional inspection. They gave us a detailed report with photos, explained what they found, and told us honestly that our furnace was good for another few years. No pressure to replace something we don't need yet.",
      author: "Jennifer W.",
      location: "Vaughan"
    },
    {
      text: "For $149, this inspection gave us peace of mind heading into winter. No cold surprises. Strongly recommend doing this before the heating season hits.",
      author: "David L.",
      location: "Aurora"
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[var(--color-slate-blue)] to-blue-800 text-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-white">
                Pre-Winter Furnace Inspection
              </h1>
              <p className="text-2xl font-bold text-[var(--color-warm-amber)] mb-4">
                Just $149
              </p>
              <p className="text-xl text-gray-100 mb-6">
                Fall is the perfect time to ensure your furnace is ready for winter. A comprehensive inspection now prevents emergency calls on the coldest night of the year.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                  <span>Complete furnace system inspection</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                  <span>Heat exchanger safety check</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                  <span>Filter replacement included</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                  <span>Gas line and vent check</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[var(--color-warm-amber)] mt-1 flex-shrink-0" />
                  <span>Detailed written report with photos</span>
                </li>
              </ul>
              <p className="text-lg text-gray-200 mb-8">
                Call (905) 555-0142 or book online to schedule your inspection.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1605281317010-fe5ffe798166?w=900&q=80&auto=format&fit=crop"
                alt="High-efficiency residential furnace ready for the heating season"
                width={900}
                height={600}
                loading="eager"
                className="rounded-lg shadow-xl mb-6 w-full object-cover aspect-[3/2]"
              />
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-8">
              <div className="space-y-4 text-white">
                <div className="flex items-center gap-3">
                  <Calendar size={28} className="text-[var(--color-warm-amber)]" />
                  <div>
                    <p className="font-semibold">Now Through November</p>
                    <p className="text-sm text-gray-200">Schedule before the heating season arrives</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-white border-opacity-20">
                  <Clock size={28} className="text-[var(--color-warm-amber)]" />
                  <div>
                    <p className="font-semibold">45-60 Minutes</p>
                    <p className="text-sm text-gray-200">Typical inspection appointment duration</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-white border-opacity-20">
                  <MapPin size={28} className="text-[var(--color-warm-amber)]" />
                  <div>
                    <p className="font-semibold">Serving York Region</p>
                    <p className="text-sm text-gray-200">Vaughan, Markham, Richmond Hill, and surrounding areas</p>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Fall Maintenance Matters */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center text-[var(--color-text-dark)]">
            Why Fall Furnace Inspection is Essential
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 bg-[var(--color-slate-blue)] text-white rounded-lg flex items-center justify-center mb-4 text-lg font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                Avoid Winter Emergencies
              </h3>
              <p className="text-gray-600">
                Most furnace failures happen in January when temperatures drop and systems work hardest. An inspection now catches problems before winter arrives.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 bg-[var(--color-slate-blue)] text-white rounded-lg flex items-center justify-center mb-4 text-lg font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                Safety First
              </h3>
              <p className="text-gray-600">
                Heat exchanger cracks, gas leaks, and ventilation problems are serious safety issues. Annual inspection ensures your home is safe.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 bg-[var(--color-slate-blue)] text-white rounded-lg flex items-center justify-center mb-4 text-lg font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">
                Maintain Warranty Coverage
              </h3>
              <p className="text-gray-600">
                Most furnace warranties require annual professional maintenance. A $149 inspection now can save you thousands if warranty coverage is needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
            Schedule Your $149 Pre-Winter Inspection
          </h2>

          {isSubmitted ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
              <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
              <p className="text-green-700">
                Your furnace inspection request has been received (demo mode). We'll contact you shortly to confirm your service date.
              </p>
              <p className="text-sm text-gray-600 mt-4 italic">
                Note: This is a portfolio demonstration site. No actual service request has been submitted.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 bg-[var(--color-neutral-bg)] rounded-lg p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                    placeholder="Jane Johnson"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                    placeholder="(905) 555-1234"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                  placeholder="123 Main St, Vaughan, ON L4L 1A1"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="systemAge" className="block text-sm font-medium text-gray-700 mb-2">
                    Furnace Age (Approximate)
                  </label>
                  <select
                    id="systemAge"
                    name="systemAge"
                    value={formData.systemAge}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                  >
                    <option value="">Select furnace age</option>
                    <option value="0-5">0-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10-15">10-15 years</option>
                    <option value="15-20">15-20 years</option>
                    <option value="20+">20+ years</option>
                    <option value="unsure">Not sure</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Service Date
                  </label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="timeWindow" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time Window
                </label>
                <select
                  id="timeWindow"
                  name="timeWindow"
                  value={formData.timeWindow}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                >
                  <option value="">Select time window</option>
                  <option value="morning">Early Morning (7:30 AM - 9:00 AM)</option>
                  <option value="mid-morning">Mid-Morning (9:00 AM - 12:00 PM)</option>
                  <option value="afternoon">Afternoon (12:00 PM - 3:00 PM)</option>
                  <option value="late-afternoon">Late Afternoon (3:00 PM - 6:00 PM)</option>
                  <option value="flexible">Flexible - I'll work around your schedule</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 italic">
                  <strong>Note:</strong> This is a demonstration form on a portfolio site. No actual service request will be submitted.
                </p>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition-colors"
                >
                  Schedule Your Inspection
                </button>
                <p className="text-sm text-gray-600 mt-4">
                  Or call us directly at <strong>(905) 555-0142</strong>
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center text-[var(--color-text-dark)]">
            What Our Customers Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-[var(--color-warm-amber)] fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <strong className="text-[var(--color-text-dark)]">{testimonial.author}</strong>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-600">{testimonial.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[var(--color-slate-blue)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Get Peace of Mind Before Winter Arrives
          </h2>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            A $149 inspection now can save thousands in emergency repairs on the coldest night of the year.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              Schedule Now
              <ArrowRight size={20} />
            </button>
            <a
              href="tel:9055550142"
              className="inline-flex items-center justify-center gap-2 bg-white text-[var(--color-slate-blue)] px-8 py-4 rounded text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Phone size={20} />
              (905) 555-0142
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
